import { APP_ENV, IS_CLIENT } from '../config/constants';

interface EventData {
  [key: string]: string | number | boolean;
}

interface EventObject extends EventData {
  event: string,
}

const gtm = {
  // Track wether events are binded already or not.
  hasEventsAttached: false as boolean,

  // If GTM is not loaded we will push all events to this variable. Once GTM is
  // loaded we will concat this array with the GTM dataLayer variable.
  tempDataLayer: [] as EventObject[],

  // -----------------------------------------------------------------------------
  //
  // Define our internal helper functions.
  //
  // -----------------------------------------------------------------------------

  /**
   * gtm.loaded
   *
   * @description
   *   Check wether google tag manager has been loaded.
   * @return {bool}
   *   A boolean wether the GTM is loaded or not.
   */
  loaded: () => {
    return typeof window.google_tag_manager !== 'undefined';
  },

  /**
   * gtm.push
   *
   * @description
   *   A wrapper function to only pusb data into the dataLayer if GTM is loaded.
   * @param {object} data
   *   Event data to be sent to GTM.
   * @return {array|bool}
   *   An array containing the new dataLayer result or `false`.
   */
  push: (data: EventObject) => {
    if (gtm.loaded()) {
      return window.dataLayer.push(data);
    }
    gtm.tempDataLayer.push(data);

    return false;
  },

  /**
   * gtm.attachEventListeners
   *
   * @description
   *   Attach event listeners related to GTM.
   * @return {void}
   */
  attachEventListeners: () => {
    if (IS_CLIENT && !gtm.hasEventsAttached) {
      window.addEventListener('GTM-Loaded', () => {
        for (let i = 0, len = gtm.tempDataLayer.length; i < len; i++) {
          window.dataLayer.push(gtm.tempDataLayer[i]);
        }

        gtm.tempDataLayer = [];
      });

      gtm.hasEventsAttached = true;
    }
  },

  // -----------------------------------------------------------------------------
  //
  // Register our wrapper functions. We will use these throughout the app.
  //
  // -----------------------------------------------------------------------------

  trackPage: (url: string) => {
    if (url) {
      gtm.push({
        event: 'Widgets - Track page',
        location: url,
      });
    }
  },


  trackUserEvent: (widget: string, action: string, label: string, name?: string) => {
    const eventObject: EventObject = {
      event: 'Widgets - Track user event',
      eventWidget: widget,
      eventAction: action,
      eventLabel: label,
      eventName: name || false, // We use the boolean to filter out stuff on the GTM side.
    };

    if (APP_ENV === 'development') { console.log(eventObject); }

    gtm.push(eventObject);
  },

  // Preferably the `trackUserEvent` function is used for sending user events to GTM.
  // Though this function is still here for the allready existing uses of it.
  legacyTrackUserEvent: (event: string, data: EventData) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      ...data,
    });
  },
};

// Initialize GTM.
gtm.attachEventListeners();

export default gtm;
